import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, TrendingUp, Briefcase, Users, Gavel, IndianRupee, CheckSquare, AlertCircle } from "lucide-react";
import { Field, Input, GoldButton, PageHeader } from "@/components/Ui";
import { fmtDate } from "@/lib/time";

const fmtINR = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Analytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const today = new Date().toISOString().slice(0, 10);
    const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
    const [range, setRange] = useState({ date_from: yearAgo, date_to: today });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/analytics/overview", { params: range });
            setData(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

    const exportCsv = async () => {
        try {
            const res = await api.get("/analytics/export.csv", { params: range, responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `analytics-${range.date_from}_to_${range.date_to}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
            toast.success("Analytics exported");
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader
                title="Analytics"
                subtitle="Multi-dimensional snapshot of your practice — cases, hearings, revenue, clients, productivity. Pivot by date, export to Excel for board reviews."
                action={
                    <div className="flex gap-2 items-center flex-wrap">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Input type="date" value={range.date_from} onChange={e => setRange(r => ({ ...r, date_from: e.target.value }))} className="!py-1.5 !text-xs" data-testid="analytics-from"/>
                            <span>→</span>
                            <Input type="date" value={range.date_to} onChange={e => setRange(r => ({ ...r, date_to: e.target.value }))} className="!py-1.5 !text-xs" data-testid="analytics-to"/>
                        </div>
                        <GoldButton onClick={exportCsv} data-testid="analytics-export-btn">
                            <Download className="w-4 h-4 inline mr-1.5" strokeWidth={2}/>Export
                        </GoldButton>
                    </div>
                }
            />

            {loading && !data && <div className="text-xs text-muted-foreground py-10 text-center">Crunching numbers…</div>}

            {data && (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="analytics-kpis">
                        <Kpi icon={Briefcase} label="New litigations" value={data.kpis.new_cases}/>
                        <Kpi icon={Users} label="New clients" value={data.kpis.new_clients}/>
                        <Kpi icon={Gavel} label="Hearings" value={data.kpis.hearings}/>
                        <Kpi icon={IndianRupee} label="Revenue" value={fmtINR(data.kpis.revenue)} accent/>
                        <Kpi icon={IndianRupee} label="Billed" value={fmtINR(data.kpis.billed)}/>
                        <Kpi icon={AlertCircle} label="Outstanding" value={fmtINR(data.kpis.outstanding)} warn={data.kpis.outstanding > 0}/>
                        <Kpi icon={CheckSquare} label="Tasks open" value={data.kpis.tasks_open}/>
                        <Kpi icon={CheckSquare} label="Tasks completed" value={data.kpis.tasks_done}/>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-5">
                        <BarChart title="Cases by stage" rows={data.cases_by_stage} testId="chart-stage"/>
                        <BarChart title="Cases by court" rows={data.cases_by_court} testId="chart-court"/>
                        <BarChart title="Revenue by month" rows={data.revenue_by_month.map(x => ({ label: x.label, value: x.value }))} fmt={fmtINR} testId="chart-revenue"/>
                        <BarChart title="Top clients by revenue" rows={data.top_clients} fmt={fmtINR} testId="chart-clients"/>
                    </div>

                    <div className="mt-6 text-[10px] text-muted-foreground text-right">
                        Range: {fmtDate(range.date_from)} → {fmtDate(range.date_to)} · Snapshot generated live
                    </div>
                </>
            )}
        </>
    );
}

function Kpi({ icon: Icon, label, value, accent, warn }) {
    return (
        <div className={`border bg-muted p-4 ${accent ? "border-primary/40" : warn ? "border-amber-700/50" : "border-border"}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <Icon className="w-3 h-3" strokeWidth={1.5}/> {label}
            </div>
            <div className={`mt-2 font-serif text-2xl ${accent ? "text-primary" : warn ? "text-amber-500" : "text-foreground"}`}>{value ?? "—"}</div>
        </div>
    );
}

function BarChart({ title, rows, fmt, testId }) {
    const max = Math.max(...rows.map(r => Number(r.value) || 0), 1);
    const _fmt = fmt || ((v) => v);
    return (
        <div className="bg-muted border border-border p-5" data-testid={testId}>
            <div className="overline mb-3">{title}</div>
            {rows.length === 0 ? (
                <div className="text-[10px] text-muted-foreground py-6 text-center">No data in this range.</div>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => (
                        <div key={r.label} className="flex items-center gap-3 text-xs">
                            <div className="w-32 truncate text-muted-foreground shrink-0" title={r.label}>{r.label}</div>
                            <div className="flex-1 bg-background h-5 overflow-hidden">
                                <div className="h-full bg-primary/70" style={{ width: `${((Number(r.value) || 0) / max) * 100}%` }}/>
                            </div>
                            <div className="w-24 text-right font-mono text-primary shrink-0">{_fmt(r.value)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
